import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { DateRangePickerProps } from 'components/DateRangePicker';
import { fixPagination } from 'utils';
import BasicPagination from './BasicPagination';
import PaginationLimitSelect from './PaginationLimitSelect';
import { useStyles } from './styles';
import { ChangeListener, PaginationLabels, Value } from './types';
import { getPaginationSetter } from './utils';
import { DEFAULT_PAGINATION_VALUE } from './constants';
import DataSelect, { IDataSelect } from './components/DataSelect';
import ShowTypeSelect, { ShowTypeSelectProps } from './components/ShowTypeSelect';
import { useSelector } from 'react-redux';
import { AppState } from '../../store';
import { UserRolesEnum } from 'utils/types';

export type IPaginationWithYearAndMonth = {
  value?: Value;
  defaultValue?: Value;
  count: number;
  labels?: PaginationLabels;
  dateRangePickerProps: DateRangePickerProps;
  showTypeSelectProps: ShowTypeSelectProps;
  yearSelectProps: IDataSelect;
  monthSelectProps: IDataSelect;
  onChange?: ChangeListener;
  accountTypeSelect?: ReactNode;
};

const PaginationWithDateRange: FC<IPaginationWithYearAndMonth> = ({
  value,
  defaultValue,
  count = 0,
  onChange,
  labels,
  showTypeSelectProps,
  yearSelectProps,
  monthSelectProps,
}) => {
  const styles = useStyles();

  const isAdmin: boolean = useSelector(
    (state: AppState) => !!state.session.user && state.session.user.role === UserRolesEnum.admin,
  );
  const isGlobalUser: boolean = useSelector(
    (state: AppState) => !!state.session.user && state.session.user.role === UserRolesEnum.globalUser,
  );

  const [innerValue, setInnerValue] = useState(null as unknown as Value);
  const selectedValue = useMemo(
    () => value || innerValue || defaultValue || DEFAULT_PAGINATION_VALUE,
    [defaultValue, innerValue, value],
  );

  const applyChanges: ChangeListener = useCallback(
    (newValue: Value) => {
      setInnerValue(newValue);
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange],
  );

  const setPagination = useMemo(() => getPaginationSetter(applyChanges, selectedValue), [applyChanges, selectedValue]);

  return (
    <div className={`${styles.paginationWrapperInnerContent} ${styles.paginationWrapperInnerContentPadding}`}>
      {(isAdmin || isGlobalUser) && <ShowTypeSelect {...showTypeSelectProps} />}
      <DataSelect {...monthSelectProps} />
      <DataSelect {...yearSelectProps} />
      <PaginationLimitSelect
        value={selectedValue.limit}
        count={count}
        label={labels?.limitSelectLabel}
        onChange={(selected): void => setPagination(fixPagination({ ...selectedValue, limit: selected }, count))}
      />
      <BasicPagination value={selectedValue} count={count} label={labels?.itemCountLabel} onChange={setPagination} />
    </div>
  );
};

export default PaginationWithDateRange;
